import Papa from 'papaparse';
import { District, Location, Measurement, DistrictStats, TemperatureStats, MeteoSchweizData } from '../types';

export class CSVDataService {
  private static districts: District[] = [];
  private static locations: Location[] = [];
  private static measurements: Measurement[] = [];
  private static users: any[] = [];
  private static meteoSchweizData: MeteoSchweizData[] = [];
  private static isLoaded = false;

  static async loadAllData(): Promise<void> {
    if (this.isLoaded) return;

    try {
      // Load CSV files from the public folder
      const [districtsData, locationsData, measurementsData, usersData, meteoSchweizData] = await Promise.all([
        this.loadCSV('/data/districts_rows.csv'),
        this.loadCSV('/data/locations_rows.csv'),
        this.loadCSV('/data/measurements_rows.csv'),
        this.loadCSV('/data/users_rows.csv'),
        this.loadCSV('/data/250901_MeteoSchweiz_Daten.csv'),
      ]);

      this.districts = this.parseDistricts(districtsData);
      this.locations = this.parseLocations(locationsData);
      this.measurements = this.parseMeasurements(measurementsData);
      this.users = this.parseUsers(usersData);
      this.meteoSchweizData = this.parseMeteoSchweizData(meteoSchweizData);
      this.isLoaded = true;

      console.log('CSV data loaded successfully:', {
        districts: this.districts.length,
        locations: this.locations.length,
        measurements: this.measurements.length,
      });
    } catch (error) {
      console.error('Error loading CSV data:', error);
      // Fallback to mock data if CSV loading fails
      this.loadMockData();
    }
  }

  private static async loadCSV(filePath: string): Promise<any[]> {
    return new Promise((resolve, reject) => {
      Papa.parse(filePath, {
        download: true,
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          if (results.errors.length > 0) {
            console.warn('CSV parsing warnings:', results.errors);
          }
          resolve(results.data);
        },
        error: (error) => {
          reject(error);
        },
      });
    });
  }

  private static parseDistricts(data: any[]): District[] {
    return data.map(row => ({
      id: parseInt(row.id),
      name: row.name,
    }));
  }

  private static parseLocations(data: any[]): Location[] {
    return data.map(row => ({
      id: parseInt(row.id),
      district_id: parseInt(row.district_id),
      name: row.name,
      lat: parseFloat(row.lat),
      lng: parseFloat(row.lng),
      description: row.description || '',
      hot_cool: row.hot_cool as 'Hot' | 'Cool',
      type: row.type || '',
    }));
  }

  private static parseMeasurements(data: any[]): Measurement[] {
    return data.map(row => ({
      id: parseInt(row.id),
      user_id: row.user_id,
      district_id: parseInt(row.district_id),
      location_id: parseInt(row.location_id),
      sensor_id: parseInt(row.sensor_id),
      perceived_temperature: row.perceived_temperature,
      environment_observations: row.environment_observations || '',
      measured_temperature: parseFloat(row.measured_temperature) || 0,
      notes: row.notes || '',
      created_at: row.created_at,
      weather_conditions: this.parseWeatherConditions(row.weather_conditions),
    }));
  }

  private static parseUsers(data: any[]): any[] {
    return data.map(row => ({
      id: row.id,
      email: row.email,
      onboarded: row.onboarded === 'true',
      comfort_answer: row.comfort_answer,
      created_at: row.created_at,
      primary_district_id: row.primary_district_id ? parseInt(row.primary_district_id) : null,
      data_protection_accepted: row.data_protection_accepted === 'true',
      sensor_id: row.sensor_id ? parseInt(row.sensor_id) : null,
    }));
  }

  private static parseWeatherConditions(conditions: string): string[] {
    try {
      // Handle JSON array format
      if (conditions && conditions.startsWith('[') && conditions.endsWith(']')) {
        return JSON.parse(conditions);
      }
      // Handle comma-separated format
      if (conditions && conditions.includes(',')) {
        return conditions.split(',').map(c => c.trim().replace(/"/g, ''));
      }
      // Single condition
      if (conditions) {
        return [conditions.trim().replace(/"/g, '')];
      }
      return [];
    } catch (error) {
      console.warn('Error parsing weather conditions:', conditions, error);
      return [];
    }
  }

  private static parseMeteoSchweizData(data: any[]): MeteoSchweizData[] {
    return data.map(row => ({
      station_abbr: row.station_abbr,
      reference_timestamp: row.reference_timestamp,
      tre200h0: parseFloat(row.tre200h0) || 0,
    }));
  }

  private static loadMockData(): void {
    // Fallback mock data if CSV loading fails
    this.districts = [
      { id: 1, name: 'Adligenswil' },
      { id: 2, name: 'Luzern' },
      { id: 3, name: 'Horw' },
      { id: 4, name: 'Meggen' },
      { id: 5, name: 'Ebikon' },
      { id: 6, name: 'Malters' },
      { id: 7, name: 'Kriens' },
      { id: 8, name: 'Emmen' },
    ];

    this.locations = [
      { id: 1, district_id: 1, name: 'Teufmatt', lat: 47.0716736526683, lng: 8.36422474813604, description: 'Beim Monument / Kunstwerk', type: 'Citizen Scientist' },
      { id: 11, district_id: 2, name: 'Bahnhofplatz', lat: 47.0507265898843, lng: 8.31021467998682, description: 'Unter Torbogen, im Schatten', type: 'Citizen Scientist' },
    ];

    this.measurements = [
      { id: 1, user_id: 'user1', district_id: 1, location_id: 1, sensor_id: 101, perceived_temperature: 'Warm', environment_observations: 'Sonnig', measured_temperature: 28.5, notes: 'Direkte Sonneneinstrahlung', created_at: '2025-08-18 12:00:00+00', weather_conditions: ['Die Sonne scheint'] },
    ];
  }

  static getDistricts(): District[] {
    return this.districts;
  }

  static getLocations(): Location[] {
    return this.locations;
  }

  static getMeasurements(): Measurement[] {
    return this.measurements;
  }

  static getMeteoSchweizData(): MeteoSchweizData[] {
    return this.meteoSchweizData;
  }

  static getMeasurementsWithUserComfort(): (Measurement & { user_comfort_answer?: string })[] {
    return this.measurements.map(measurement => {
      const user = this.users.find(u => u.id === measurement.user_id);
      return {
        ...measurement,
        user_comfort_answer: user?.comfort_answer || undefined,
      };
    });
  }

  static getDistrictStats(districtId?: number): DistrictStats[] {
    const stats: DistrictStats[] = [];

    // Handle special case for "All Districts" (districtId: 0)
    if (districtId === 0) {
      const allMeasurements = this.measurements; // Show all measurements, not just those with temp > 0
      const allLocations = this.locations;
      
      if (allMeasurements.length === 0) return stats;

      const temps = allMeasurements.map(m => m.measured_temperature).filter(t => t > 0);
      const perceivedTemps = allMeasurements
        .map(m => this.parsePerceivedTemperature(m.perceived_temperature))
        .filter(t => t !== null) as number[];

      if (temps.length === 0) return stats;

      stats.push({
        districtId: 0,
        districtName: 'Alle Gemeinden',
        totalMeasurements: allMeasurements.length,
        avgMeasuredTemp: temps.reduce((a, b) => a + b, 0) / temps.length,
        avgPerceivedTemp: perceivedTemps.length > 0 ? perceivedTemps.reduce((a, b) => a + b, 0) / perceivedTemps.length : 0,
        maxTemp: Math.max(...temps),
        minTemp: Math.min(...temps),
        locations: allLocations,
      });
      
      return stats;
    }



    // Handle individual districts
    for (const district of this.districts) {
      if (districtId && district.id !== districtId) continue;

      const districtMeasurements = this.measurements.filter(m => m.district_id === district.id);
      const districtLocations = this.locations.filter(l => l.district_id === district.id);

      if (districtMeasurements.length === 0) continue;

      const temps = districtMeasurements.map(m => m.measured_temperature).filter(t => t > 0);
      const perceivedTemps = districtMeasurements
        .map(m => this.parsePerceivedTemperature(m.perceived_temperature))
        .filter(t => t !== null) as number[];

      if (temps.length === 0) continue;

      stats.push({
        districtId: district.id,
        districtName: district.name,
        totalMeasurements: districtMeasurements.length,
        avgMeasuredTemp: temps.reduce((a, b) => a + b, 0) / temps.length,
        avgPerceivedTemp: perceivedTemps.length > 0 ? perceivedTemps.reduce((a, b) => a + b, 0) / perceivedTemps.length : 0,
        maxTemp: Math.max(...temps),
        minTemp: Math.min(...temps),
        locations: districtLocations,
      });
    }

    return stats;
  }

  static getTemperatureStats(districtId: number, locationId?: number): TemperatureStats {
    let filteredMeasurements: Measurement[];
    
    // Handle special case for "All Districts" (districtId: 0)
    if (districtId === 0) {
      filteredMeasurements = this.measurements.filter(m => m.measured_temperature > 0);
    } else {
      filteredMeasurements = this.measurements.filter(m => m.district_id === districtId && m.measured_temperature > 0);
    }
    
    if (locationId) {
      filteredMeasurements = filteredMeasurements.filter(m => m.location_id === locationId);
    }

    // Group by time of day based on actual measurement windows
    const morning = filteredMeasurements.filter(m => {
      const date = new Date(m.created_at);
      const hour = date.getHours();
      const minute = date.getMinutes();
      const timeInMinutes = hour * 60 + minute;
      // 6:30-8:30 = 390-510 minutes
      return timeInMinutes >= 390 && timeInMinutes < 510;
    });
    
    const midday = filteredMeasurements.filter(m => {
      const date = new Date(m.created_at);
      const hour = date.getHours();
      const minute = date.getMinutes();
      const timeInMinutes = hour * 60 + minute;
      // 12:00-13:30 = 720-810 minutes
      return timeInMinutes >= 720 && timeInMinutes < 810;
    });
    
    const afternoon = filteredMeasurements.filter(m => {
      const date = new Date(m.created_at);
      const hour = date.getHours();
      const minute = date.getMinutes();
      const timeInMinutes = hour * 60 + minute;
      // 16:30-18:30 = 990-1110 minutes
      return timeInMinutes >= 990 && timeInMinutes < 1110;
    });

    const calculateAvg = (ms: Measurement[]) => 
      ms.length > 0 ? ms.reduce((sum, m) => sum + m.measured_temperature, 0) / ms.length : 0;

    const calculateMax = (ms: Measurement[]) => 
      ms.length > 0 ? Math.max(...ms.map(m => m.measured_temperature)) : 0;

    const calculateMin = (ms: Measurement[]) => 
      ms.length > 0 ? Math.min(...ms.map(m => m.measured_temperature)) : 0;

    return {
      measured: {
        morning: calculateAvg(morning),
        midday: calculateAvg(midday),
        afternoon: calculateAvg(afternoon),
      },
      perceived: {
        morning: calculateAvg(morning), // Simplified - would need perceived temp conversion
        midday: calculateAvg(midday),
        afternoon: calculateAvg(afternoon),
      },
      maxMeasured: {
        morning: calculateMax(morning),
        midday: calculateMax(midday),
        afternoon: calculateMax(afternoon),
      },
      minMeasured: {
        morning: calculateMin(morning),
        midday: calculateMin(midday),
        afternoon: calculateMin(afternoon),
      },
      maxPerceived: {
        morning: calculateMax(morning), // Simplified
        midday: calculateMax(midday),
        afternoon: calculateMax(afternoon),
      },
    };
  }

  private static parsePerceivedTemperature(perceived: string): number | null {
    const tempMap: { [key: string]: number } = {
      'Sehr kalt': 4,
      'Kalt': 8,
      'Kühl': 13,
      'Leicht kühl': 18,
      'Behaglich': 20,
      'Leicht warm': 23,
      'Warm': 29,
      'Heiss': 35,
      'Sehr heiss': 41,
    };
    return tempMap[perceived] || null;
  }
}
